import React, { useState } from 'react';
import axios from 'axios';
import { Factory, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TokenFactory = () => {
  const [form, setForm] = useState({
    symbol: '',
    name: '',
    totalSupply: '',
    creatorAddress: '',
    decimals: 18
  });
  const [loading, setLoading] = useState(false);

  const createToken = async () => {
    if (!form.symbol || !form.name || !form.totalSupply || !form.creatorAddress) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API}/token/create`, {
        symbol: form.symbol.toUpperCase(),
        name: form.name,
        total_supply: parseFloat(form.totalSupply),
        creator_address: form.creatorAddress,
        decimals: parseInt(form.decimals)
      });
      toast.success(`Token ${form.symbol} created successfully!`);
      setForm({
        symbol: '',
        name: '',
        totalSupply: '',
        creatorAddress: '',
        decimals: 18
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create token');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="font-unbounded text-4xl font-bold text-text-primary mb-2">
          Token Factory
        </h1>
        <p className="text-text-secondary">Create custom tokens on the blockchain</p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl glass p-8 rounded-sm"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-secondary/10 rounded-sm">
            <Factory size={28} className="text-secondary" strokeWidth={1.5} />
          </div>
          <div>
            <h2 className="font-unbounded text-2xl font-bold text-text-primary">
              Create New Token
            </h2>
            <p className="text-text-muted text-sm">Launch your custom token</p>
          </div>
        </div>

        <div className="space-y-5">
          <div>
            <label className="text-text-secondary text-sm mb-2 block font-medium">
              Token Symbol *
            </label>
            <input
              type="text"
              value={form.symbol}
              onChange={(e) => setForm({ ...form, symbol: e.target.value })}
              placeholder="e.g., MYT"
              data-testid="token-symbol-input"
              className="w-full p-3 bg-background-subtle border border-border rounded-sm text-text-primary focus:border-primary focus:outline-none uppercase"
            />
          </div>

          <div>
            <label className="text-text-secondary text-sm mb-2 block font-medium">
              Token Name *
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., My Token"
              data-testid="token-name-input"
              className="w-full p-3 bg-background-subtle border border-border rounded-sm text-text-primary focus:border-primary focus:outline-none"
            />
          </div>

          <div>
            <label className="text-text-secondary text-sm mb-2 block font-medium">
              Total Supply *
            </label>
            <input
              type="number"
              value={form.totalSupply}
              onChange={(e) => setForm({ ...form, totalSupply: e.target.value })}
              placeholder="1000000"
              data-testid="token-supply-input"
              className="w-full p-3 bg-background-subtle border border-border rounded-sm text-text-primary focus:border-primary focus:outline-none"
            />
          </div>

          <div>
            <label className="text-text-secondary text-sm mb-2 block font-medium">
              Creator Address *
            </label>
            <input
              type="text"
              value={form.creatorAddress}
              onChange={(e) => setForm({ ...form, creatorAddress: e.target.value })}
              placeholder="Wallet address that will receive tokens"
              data-testid="creator-address-input"
              className="w-full p-3 bg-background-subtle border border-border rounded-sm text-text-primary focus:border-primary focus:outline-none font-mono text-sm"
            />
          </div>

          <div>
            <label className="text-text-secondary text-sm mb-2 block font-medium">
              Decimals
            </label>
            <input
              type="number"
              value={form.decimals}
              onChange={(e) => setForm({ ...form, decimals: e.target.value })}
              data-testid="token-decimals-input"
              className="w-full p-3 bg-background-subtle border border-border rounded-sm text-text-primary focus:border-primary focus:outline-none"
            />
          </div>

          <button
            onClick={createToken}
            disabled={loading}
            data-testid="create-token-btn"
            className="w-full py-4 bg-secondary text-secondary-foreground font-bold rounded-sm hover:opacity-90 transition-opacity duration-200 flex items-center justify-center gap-2 disabled:opacity-50 mt-6"
          >
            <Plus size={20} />
            {loading ? 'Creating Token...' : 'Create Token'}
          </button>
        </div>

        <div className="mt-6 p-4 bg-background-subtle border border-border rounded-sm">
          <p className="text-xs text-text-muted">
            <strong className="text-accent-warning">Note:</strong> Once created, the total supply
            will be sent to the creator address. You'll need to mine a block to confirm the token
            creation.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default TokenFactory;
